import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { FieldErrors, FieldValues, Path, UseFormSetError } from 'react-hook-form';
import { api, ApiError, errorMessage, keys, useHelpTexts, useMe, useMeta } from '../api.ts';
import type { Country } from '../../shared/types.ts';

/* ---------- ilmoitukset (toast) ---------- */

type Toast = { id: number; kind: 'ok' | 'error'; text: string };
const ToastCtx = createContext<(kind: Toast['kind'], text: string) => void>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback((kind: Toast['kind'], text: string) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, kind, text }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), kind === 'error' ? 7000 : 4000);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.kind}`}>
            {t.text}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export function useToast() {
  const push = useContext(ToastCtx);
  return useMemo(() => ({ ok: (t: string) => push('ok', t), error: (e: unknown) => push('error', errorMessage(e)) }), [push]);
}

/** Vie palvelimen kenttäkohtaiset virheet react-hook-formiin */
export function applyServerErrors<T extends FieldValues>(err: unknown, setError: UseFormSetError<T>): boolean {
  if (!(err instanceof ApiError) || !err.issues.length) return false;
  for (const i of err.issues) setError(i.path as Path<T>, { message: i.message });
  return true;
}

/* ---------- perusosat ---------- */

export function Spinner() {
  return <span className="spinner" aria-label="Ladataan" />;
}

export function Loading({ text = 'Ladataan…' }: { text?: string }) {
  return (
    <div className="empty">
      <Spinner /> <span style={{ marginLeft: 8 }}>{text}</span>
    </div>
  );
}

export function ErrorBox({ error }: { error: unknown }) {
  if (!error) return null;
  return <div className="alert alert-error">{errorMessage(error)}</div>;
}

export function Field({
  label,
  error,
  hint,
  children,
  htmlFor,
}: {
  label: ReactNode;
  error?: { message?: string } | undefined;
  hint?: ReactNode;
  children: ReactNode;
  htmlFor?: string;
}) {
  return (
    <div className="field">
      <label htmlFor={htmlFor}>{label}</label>
      {children}
      {error?.message ? <span className="error">{error.message}</span> : hint ? <span className="hint">{hint}</span> : null}
    </div>
  );
}

export function fieldError<T extends FieldValues>(errors: FieldErrors<T>, name: keyof T) {
  return errors[name] as { message?: string } | undefined;
}

export function PageHeader({ title, sub, actions }: { title: ReactNode; sub?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="page-header">
      <div>
        <h1>{title}</h1>
        {sub && <div className="sub">{sub}</div>}
      </div>
      {actions && <div className="row">{actions}</div>}
    </div>
  );
}

export function Tabs({ items }: { items: Array<{ to: string; label: string; end?: boolean } | false | null | undefined> }) {
  return (
    <nav className="tabs">
      {items.filter(Boolean).map((t) => {
        const item = t as { to: string; label: string; end?: boolean };
        return (
          <NavLink key={item.to} to={item.to} end={item.end ?? true}>
            {item.label}
          </NavLink>
        );
      })}
    </nav>
  );
}

export function CallLink({ id }: { id: string }) {
  return (
    <Link className="call" to={`/users/${encodeURIComponent(id)}`}>
      {id}
    </Link>
  );
}

/* ---------- vahvistusdialogi ---------- */

export function ConfirmButton({
  children,
  title,
  message,
  confirmText = 'Kyllä',
  onConfirm,
  className = 'btn btn-danger',
  disabled,
}: {
  children: ReactNode;
  title: string;
  message: ReactNode;
  confirmText?: string;
  onConfirm: () => void | Promise<unknown>;
  className?: string;
  disabled?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [busy, setBusy] = useState(false);
  return (
    <>
      <button type="button" className={className} disabled={disabled} onClick={() => ref.current?.showModal()}>
        {children}
      </button>
      <dialog ref={ref} className="modal" onClose={() => setBusy(false)}>
        <h2>{title}</h2>
        <div className="muted" style={{ marginBottom: 18 }}>
          {message}
        </div>
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-danger"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await onConfirm();
              } finally {
                ref.current?.close();
              }
            }}
          >
            {busy ? <Spinner /> : confirmText}
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => ref.current?.close()}>
            Peruuta
          </button>
        </div>
      </dialog>
    </>
  );
}

/* ---------- ohjetekstit (ylläpitäjä voi muokata paikan päällä) ---------- */

export function Help({ name }: { name: string }) {
  const { data: texts } = useHelpTexts();
  const { data: me } = useMe();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const qc = useQueryClient();
  const toast = useToast();
  const text = texts?.[name] ?? '';
  const save = useMutation({
    mutationFn: () => api.put(`/help/${encodeURIComponent(name)}`, { text: draft }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.help });
      setEditing(false);
      toast.ok('Ohje tallennettu');
    },
    onError: toast.error,
  });

  if (!text.trim() && !me?.isAdmin) return null;
  return (
    <>
      <button
        type="button"
        className="btn icon-btn help-toggle"
        title="Ohje"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        ?
      </button>
      {open && (
        <div className="help-panel" style={{ flexBasis: '100%' }}>
          {editing ? (
            <div className="stack">
              <textarea value={draft} rows={8} onChange={(e) => setDraft(e.target.value)} />
              <div className="form-actions">
                <button className="btn btn-primary btn-sm" onClick={() => save.mutate()} disabled={save.isPending}>
                  Tallenna ohje
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => setEditing(false)}>
                  Peruuta
                </button>
              </div>
            </div>
          ) : (
            <>
              {text || <span className="faint">Ohjetta ei ole vielä kirjoitettu.</span>}
              {me?.isAdmin && (
                <div style={{ marginTop: 10 }}>
                  <button
                    className="btn btn-sm"
                    onClick={() => {
                      setDraft(text);
                      setEditing(true);
                    }}
                  >
                    Muokkaa ohjetta
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
}

/* ---------- valitsimet ---------- */

export function ChipSelect<T extends string | number>({
  options,
  value,
  onChange,
  isParent,
}: {
  options: Array<{ value: T; label: string }>;
  value: T[];
  onChange: (v: T[]) => void;
  isParent?: (v: T) => boolean;
}) {
  const set = new Set(value);
  return (
    <div className="chips">
      {options.map((o) => (
        <button
          type="button"
          key={String(o.value)}
          className={`chip${set.has(o.value) ? ' on' : ''}${isParent?.(o.value) ? ' parent' : ''}`}
          aria-pressed={set.has(o.value)}
          onClick={() => onChange(set.has(o.value) ? value.filter((v) => v !== o.value) : [...value, o.value])}
        >
          {o.label}
        </button>
      ))}
      {value.length > 0 && (
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => onChange([])}>
          Tyhjennä
        </button>
      )}
    </div>
  );
}

export function BandPicker({ value, onChange }: { value: number[]; onChange: (v: number[]) => void }) {
  const { data } = useMeta();
  return <ChipSelect options={(data?.bands ?? []).map((b) => ({ value: b.band, label: b.label }))} value={value} onChange={onChange} />;
}

export function ModePicker({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const { data } = useMeta();
  const modes = data?.modes ?? [];
  const parents = new Set(modes.filter((m) => !m.parentMode).map((m) => m.mode));
  return (
    <ChipSelect
      options={modes.map((m) => ({ value: m.mode, label: m.mode }))}
      value={value}
      onChange={onChange}
      isParent={(v) => parents.has(v)}
    />
  );
}

export function countryLabel(c: Country) {
  return `${c.code} – ${c.name}${c.deleted ? ' (poistunut)' : ''}`;
}

/** Hakukentällinen monivalinta maille */
export function CountryPicker({ value, onChange }: { value: number[]; onChange: (v: number[]) => void }) {
  const { data } = useMeta();
  const [q, setQ] = useState('');
  const set = new Set(value);
  const list = (data?.countries ?? []).filter((c) => {
    const s = q.trim().toLowerCase();
    return !s || c.name.toLowerCase().includes(s) || c.code.toLowerCase().startsWith(s);
  });
  const selected = (data?.countries ?? []).filter((c) => set.has(c.id));
  const toggle = (id: number) => onChange(set.has(id) ? value.filter((v) => v !== id) : [...value, id]);
  return (
    <div className="stack">
      {selected.length > 0 && (
        <div className="chips">
          {selected.map((c) => (
            <button key={c.id} type="button" className="chip on" onClick={() => toggle(c.id)} title="Poista valinta">
              {c.code} {c.name} ×
            </button>
          ))}
        </div>
      )}
      <div className="picker">
        <input className="input picker-search" placeholder="Hae maata tai prefiksiä…" value={q} onChange={(e) => setQ(e.target.value)} />
        <div className="picker-list">
          {['current', 'deleted'].map((grp) => {
            const items = list.filter((c) => (grp === 'deleted') === c.deleted);
            if (!items.length) return null;
            return (
              <div key={grp}>
                <div className="picker-group">{grp === 'deleted' ? 'Poistuneet' : 'Nykymaat'}</div>
                {items.map((c) => (
                  <label key={c.id} className="picker-item">
                    <input type="checkbox" checked={set.has(c.id)} onChange={() => toggle(c.id)} />
                    <span className="mono">{c.code}</span> {c.name}
                  </label>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/** Yksittäisen maan valinta (natiivi select: kirjaimen näppäily hyppää prefiksiin kuten vanhassa) */
export function CountrySelect({
  value,
  onChange,
  invalid,
  className = '',
  id,
}: {
  value: number | null | undefined;
  onChange: (v: number | null) => void;
  invalid?: boolean;
  className?: string;
  id?: string;
}) {
  const { data } = useMeta();
  const countries = data?.countries ?? [];
  return (
    <select
      id={id}
      className={className}
      value={value ?? ''}
      aria-invalid={invalid}
      onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
    >
      <option value="">Valitse maa…</option>
      <optgroup label="Nykymaat">
        {countries
          .filter((c) => !c.deleted)
          .map((c) => (
            <option key={c.id} value={c.id}>
              {countryLabel(c)}
            </option>
          ))}
      </optgroup>
      <optgroup label="Poistuneet">
        {countries
          .filter((c) => c.deleted)
          .map((c) => (
            <option key={c.id} value={c.id}>
              {countryLabel(c)}
            </option>
          ))}
      </optgroup>
    </select>
  );
}

export function Pagination({ page, pages, onPage }: { page: number; pages: number; onPage: (p: number) => void }) {
  if (pages <= 1) return null;
  const nums: number[] = [];
  for (let p = Math.max(1, page - 4); p <= Math.min(pages, page + 4); p++) nums.push(p);
  return (
    <div className="pagination">
      <button className="btn btn-sm" disabled={page <= 1} onClick={() => onPage(page - 1)}>
        ‹ Edellinen
      </button>
      {nums[0]! > 1 && (
        <>
          <button className="btn btn-sm" onClick={() => onPage(1)}>
            1
          </button>
          {nums[0]! > 2 && <span className="faint">…</span>}
        </>
      )}
      {nums.map((p) => (
        <button key={p} className={`btn btn-sm${p === page ? ' current' : ''}`} onClick={() => onPage(p)}>
          {p}
        </button>
      ))}
      {nums.at(-1)! < pages && (
        <>
          {nums.at(-1)! < pages - 1 && <span className="faint">…</span>}
          <button className="btn btn-sm" onClick={() => onPage(pages)}>
            {pages}
          </button>
        </>
      )}
      <button className="btn btn-sm" disabled={page >= pages} onClick={() => onPage(page + 1)}>
        Seuraava ›
      </button>
    </div>
  );
}

export function useDocumentTitle(title: string | undefined) {
  useEffect(() => {
    document.title = title ? `${title} · DXA.fi` : 'DXA.fi – OH DX -taulukot';
  }, [title]);
}

export function useCountryMap() {
  const { data } = useMeta();
  return useMemo(() => new Map((data?.countries ?? []).map((c) => [c.id, c])), [data]);
}
